import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import DialogDownload from '../Dialog/DialogDownload';
import { ExportStatusResponse } from 'modules/export/interfaces/common-export';
import { EXPORT_STATUS_ENUM } from 'modules/export/interfaces/export.enum';
import { useCentralizedExport } from 'contexts/CentralizedExportProvider';
import { describeProgress } from 'modules/export/utils/export-progress.utils';

type DownloadFileProps = {
  isOpen: boolean;
  onClose: () => void;
  onRetry?: () => void;
  fnDownloadService: (id: string, name?: string) => Promise<void>;
  initResponse: ExportStatusResponse;
  onBackgroundDownload?: () => void;
};

const DownloadFile = ({
  isOpen,
  onClose,
  onRetry,
  initResponse,
  fnDownloadService,
  onBackgroundDownload,
}: DownloadFileProps) => {
  const { exportData } = useCentralizedExport();

  // Use the centralized export data from context directly
  // The exportData comes from the CentralizedExportProvider's state and is already the correct one
  const contextExportItem = exportData;

  // Normalize status to EXPORT_STATUS_ENUM type
  const normalizeStatus = (status: string | undefined): EXPORT_STATUS_ENUM | undefined => {
    if (!status) return undefined;

    switch (status) {
      case 'DONE':
        return EXPORT_STATUS_ENUM.DONE;
      case 'ERROR':
        return EXPORT_STATUS_ENUM.FAILED;
      case 'PENDING':
        return EXPORT_STATUS_ENUM.PENDING;
      default:
        return undefined;
    }
  };

  const contextStatus = normalizeStatus(contextExportItem?.status);
  const initStatus = normalizeStatus(initResponse?.status);

  const actualStatus = contextExportItem?.status ? contextStatus : initStatus;
  let finalStatus = actualStatus;
  if (contextExportItem?.status && contextStatus) {
    finalStatus = contextStatus;
  } else if (initStatus) {
    finalStatus = initStatus;
  }

  const actualName = contextExportItem?.name ?? initResponse?.name;

  const [errorExport, setError] = useState<any>(undefined);
  const [resetModal, setResetModal] = useState<boolean>(false);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState<boolean>(false);
  const hasTriggeredDownload = useRef<boolean>(false);
  const timeoutTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleConsole = useCallback(() => {
    onClose?.();
    setError(undefined);
    hasTriggeredDownload.current = false;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) {
      hasTriggeredDownload.current = false;
      setShowTimeoutWarning(false);
      if (timeoutTimerRef.current) {
        clearTimeout(timeoutTimerRef.current);
        timeoutTimerRef.current = null;
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && finalStatus !== EXPORT_STATUS_ENUM.DONE && finalStatus !== EXPORT_STATUS_ENUM.FAILED) {
      timeoutTimerRef.current = setTimeout(() => {
        setShowTimeoutWarning(true);
      }, 2000);

      return () => {
        if (timeoutTimerRef.current) {
          clearTimeout(timeoutTimerRef.current);
          timeoutTimerRef.current = null;
        }
      };
    }
  }, [isOpen, finalStatus]);

  const { mutate: downloadFile, error } = useMutation(
    ({ id, name }: { id: string; name?: string }) => {
      console.log('Download file mutation called:', { id, name });
      return fnDownloadService(id, name);
    },
    {
      onSuccess: () => {
        console.log('Download successful - closing modal');
        setTimeout(() => {
          onClose?.();
          setResetModal(false);
          hasTriggeredDownload.current = false;
        }, 1000);
      },
      onError: (e: any) => {
        console.log('Download error:', e);
        hasTriggeredDownload.current = false;
      },
    },
  );

  // const handleDownload = useCallback(() => {
  //   if (actualStatus === EXPORT_STATUS_ENUM.DONE && initResponse?._id && !hasTriggeredDownload.current) {
  //     hasTriggeredDownload.current = true;
  //     setError(undefined);
  //     downloadFile({ id: initResponse._id, name: actualName });
  //   }
  // }, [actualStatus, initResponse?._id, actualName, downloadFile]);

  useEffect(() => {
    if (!initResponse) {
      console.log('MAIN EFFECT: No initResponse, returning');
      return;
    }

    // Handle DONE status - download and close
    if (finalStatus === EXPORT_STATUS_ENUM.DONE && initResponse?._id && !hasTriggeredDownload.current) {
      hasTriggeredDownload.current = true;
      setError(undefined);
      downloadFile({ id: initResponse._id, name: actualName });
    }

    // Handle FAILED status - set error and close

    // Handle FAILED status
    if (finalStatus === EXPORT_STATUS_ENUM.FAILED) {
      setError(EXPORT_STATUS_ENUM.FAILED);
      setTimeout(() => {
        onClose?.();
        setResetModal(false);
        hasTriggeredDownload.current = false;
      }, 1000);
    }
  }, [finalStatus, initResponse?._id, actualName, downloadFile, onClose, exportData, initResponse]);

  if (resetModal) return null;

  return (
    <DialogDownload
      isOpen={isOpen}
      error={error || errorExport}
      onClose={handleConsole}
      onRetry={onRetry}
      status={finalStatus}
      progress={describeProgress(
        {
          status: contextExportItem?.status ?? initResponse?.status,
          createdAt: contextExportItem?.createdAt ?? initResponse?.createdAt,
          startedAt: contextExportItem?.startedAt ?? initResponse?.startedAt,
          finishedAt: contextExportItem?.finishedAt ?? initResponse?.finishedAt,
        } as any,
        contextExportItem?.type ?? (initResponse as any)?.type,
      )}
      showTimeoutWarning={showTimeoutWarning}
      onBackgroundDownload={onBackgroundDownload}
    />
  );
};

export default DownloadFile;
